import { lockBodyScroll } from "../utils/scrollLock";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import LikeButton from "../components/LikeButton";
import DownloadButton from "../components/DownloadButton";
import SaveButton from "../components/SaveButton";
import Comments from "../components/Comments";
import ImageLightbox from "../components/ImageLightbox";
import PostSkeleton from "../components/PostSkeleton";
import { likeDesign } from "../utils/likes";
import { fetchMyEngagement, subscribeToMyEngagement } from "../utils/myEngagement";
import { getBlockedUserIds } from "../utils/blocks";
import { useFocusTrap } from "../utils/focusTrap";
import { getMutedUserIds } from "../utils/mutes";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import ReportModal from "../components/ReportModal";
import PostMenu from "../components/PostMenu";
import TruncatedText from "../components/TruncatedText";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import LazyImage from "../components/LazyImage";
import Toast from "../components/Toast";
import VerifiedBadge from "../components/VerifiedBadge";
import TagInfoModal from "../components/TagInfoModal";
import { IoLockClosedOutline, IoFolderOutline, IoAdd, IoClose, IoEllipsisVertical, IoPricetag, IoFlag } from "react-icons/io5";
import { PiBookmarkSimpleFill, PiChatCircleBold } from "react-icons/pi";
import { useLanguage } from "../context/LanguageContext";
import { confirmDialog, confirmLogin, promptDialog } from "../utils/dialog";

const PAGE_SIZE = 10;

export default function Saved() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [user, setUser] = useState(getStoredUser);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [tagModalId, setTagModalId] = useState(null);
  const [reportModalId, setReportModalId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [openComments, setOpenComments] = useState({});

  // Close ⋮ menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [collections, setCollections] = useState([]);
  const [activeCollectionId, setActiveCollectionId] = useState(null); // null = "All Saved"
  const [collectionMenuOpen, setCollectionMenuOpen] = useState(null); // collection id whose ⋮ menu is open
  const collectionMenuRef = useRef(null);
  // Where the open folder menu is drawn (fixed, in screen coordinates): the
  // folder row scrolls sideways, and a menu placed inside it would be clipped.
  const [collectionMenuPos, setCollectionMenuPos] = useState(null); // { top, left }
  const folderRowRef = useRef(null);

  // With the folders on one sideways-scrolling line, the one you pick (or the
  // one just created) may be off-screen — bring it into view.
  useEffect(() => {
    const active = folderRowRef.current?.querySelector('[data-active="true"]');
    active?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [activeCollectionId, collections.length]);

  // The folder menu is drawn at fixed screen coordinates, so it would be left
  // hanging in the wrong place if anything scrolled or the screen turned.
  useEffect(() => {
    if (!collectionMenuOpen) return undefined;
    const close = () => setCollectionMenuOpen(null);
    window.addEventListener("scroll", close, true); // capture: catches the folder row scrolling too
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [collectionMenuOpen]);
  const [openCollectionPickerFor, setOpenCollectionPickerFor] = useState(null);
  const [pickerAnchorRect, setPickerAnchorRect] = useState(null);
  const [pickerMembership, setPickerMembership] = useState({});
  const [toast, setToast] = useState("");
  const membershipRequestId = useRef(0);
  const toggleMembershipBusyRef = useRef(new Set());
  const collectionPopoverRef = useRef(null);
  useFocusTrap(collectionPopoverRef, !!(openCollectionPickerFor && pickerAnchorRect));

  const sentinelRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // The folder (or "All Saved") this page is currently showing — loadMore
  // reads this to tell whether activeCollectionId changed out from under an
  // in-flight "load more" fetch (see loadMore below).
  const activeCollectionRef = useRef(activeCollectionId);
  const loadingMoreRef = useRef(false);
  // Every other feed surface hides blocked/muted users' content in both
  // directions (see utils/blocks.js) — saved designs were the one place
  // that didn't, so a design you'd saved from someone who later blocked
  // you (or whom you blocked) stayed visible here indefinitely. Fetched
  // once per user rather than kept in state, since fetchPage reads it
  // synchronously and nothing here needs a re-render when it resolves.
  const excludeIdsRef = useRef([]);

  // Close the folder ⋮ menu when clicking outside it.
  useEffect(() => {
    function handleClickOutside(e) {
      if (collectionMenuRef.current && !collectionMenuRef.current.contains(e.target)) {
        setCollectionMenuOpen(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Without this, touch-scrolling anywhere the transparent backdrop doesn't
  // itself capture can still scroll the design list underneath while the
  // "Add to folder" popover is open — only its own folder list (which has
  // its own overflow:auto) should scroll, same convention as every other
  // overlay in the app (Profile.jsx's settings menu, ShareButton, ...).
  useEffect(() => {
    if (!openCollectionPickerFor) return;
    const unlockScroll = lockBodyScroll();
    return () => {
      unlockScroll();
    };
  }, [openCollectionPickerFor]);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    }

    getUser();
  }, []);

  useEffect(() => {
    if (!user) return;

    async function loadCollections() {
      const { data, error } = await supabase
        .from("collections")
        .select("id, name")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(100);

      if (error) {
        console.log("LOAD COLLECTIONS ERROR:", error);
        return;
      }

      setCollections(data || []);
    }

    loadCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Either the plain "all saved" list, or (when a folder is selected) the
  // designs filed into that folder — same page-at-a-time shape either way.
  // Every design this returns is, by definition, currently saved (a folder
  // can only ever hold designs that are — see toggle_design_save's own
  // cascade, which removes a design from every folder the moment it's
  // unsaved) — so _savedByMe is always true here, no separate lookup needed.
  // _likedByMe still needs one, since liking is independent of saving.
  async function fetchPage(start, end) {
    let list;
    // Filtered IN the query, before .range() — not client-side afterward.
    // Filtering a fetched page's results down AFTER the range was applied
    // made a returned page shorter than PAGE_SIZE the moment it contained
    // even one blocked/muted user's design, which set hasMore to false and
    // silently ended infinite scroll early, hiding real saved content below.
    const exclude = excludeIdsRef.current;

    if (activeCollectionId) {
      let query = supabase
        .from("collection_items")
        .select("design_id, added_at, designs!inner(*, users(username, avatar_url, verified))")
        .eq("collection_id", activeCollectionId)
        .order("added_at", { ascending: false })
        .range(start, end);

      if (exclude.length > 0) {
        query = query.not("designs.user_id", "in", `(${exclude.join(",")})`);
      }

      const { data, error } = await query;

      if (error) {
        console.log("LOAD COLLECTION ITEMS ERROR:", error);
        return { list: [] };
      }

      list = (data || []).map(row => row.designs).filter(Boolean);
    } else {
      let query = supabase
        .from("designs")
        .select("*, users(username, avatar_url, verified), saves!inner(user_id)")
        .eq("saves.user_id", user.id)
        .order("created_at", { ascending: false })
        .range(start, end);

      if (exclude.length > 0) {
        query = query.not("user_id", "in", `(${exclude.join(",")})`);
      }

      const { data, error } = await query;

      if (error) {
        console.log("SAVED DESIGNS ERROR:", error);
        return { list: [] };
      }

      list = data || [];
    }

    const { likedIds } = await fetchMyEngagement(list.map(d => d.id), user.id);
    list = list.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: true }));

    return { list };
  }

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    activeCollectionRef.current = activeCollectionId;

    async function loadDesigns() {
      setLoading(true);

      const [blockedIds, mutedIds] = await Promise.all([getBlockedUserIds(user.id), getMutedUserIds(user.id)]);
      if (cancelled) return;
      excludeIdsRef.current = [...blockedIds, ...mutedIds];

      const { list } = await fetchPage(0, PAGE_SIZE - 1);

      // Switching folders (or back to "All Saved") quickly enough for two of
      // these to overlap used to let whichever resolved last win, even if
      // it was for a folder no longer selected.
      if (cancelled) return;

      setDesigns(list);
      setOffset(PAGE_SIZE);
      setHasMore(list.length === PAGE_SIZE);
      setLoading(false);
    }

    loadDesigns();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeCollectionId]);

  // Load the next PAGE_SIZE saved designs (infinite scroll).
  async function loadMore() {
    if (!user || loading || loadingMoreRef.current || !hasMore) return;
    const requestCollection = activeCollectionId;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const { list } = await fetchPage(offset, offset + PAGE_SIZE - 1);

    // The folder changed (and loadDesigns already reset the list) while this
    // page was still in flight — appending it now would tack the OLD
    // folder's designs onto the NEW folder's freshly-reset list. Clearing
    // loadingMore here too matters: left stuck true, every future loadMore()
    // call on the new folder would return immediately (its own guard checks
    // loadingMore), permanently breaking infinite scroll until reload.
    if (requestCollection !== activeCollectionRef.current) {
      loadingMoreRef.current = false;
      setLoadingMore(false);
      return;
    }

    setDesigns(prev => [...prev, ...list]);
    setOffset(prev => prev + PAGE_SIZE);
    setHasMore(list.length === PAGE_SIZE);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, offset, hasMore, loadingMore, user, activeCollectionId]);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* still has each one saved (or liked) is a separate, per-user
  // concern — see the myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);

  // Live updates for this viewer's OWN likes/saves — e.g. unsaving something
  // from another tab or device signed into the same account. This page's
  // whole point is "designs I've saved", so an unsave (from anywhere) has to
  // drop the card immediately rather than just flip a flag on it — a plain
  // field merge would leave it sitting in the list still showing as saved.
  useEffect(() => {
    if (!user) return undefined;

    return subscribeToMyEngagement(user.id, {
      onLike: id => setDesigns(prev => prev.map(d => (d.id === id ? { ...d, _likedByMe: true } : d))),
      onUnlike: id => setDesigns(prev => prev.map(d => (d.id === id ? { ...d, _likedByMe: false } : d))),
      onUnsave: id => setDesigns(prev => prev.filter(d => d.id !== id))
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d => (d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d))
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];
    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));
    if (isCurrentlyOpen) refreshCommentCount(id);
  }

  function requireLogin(action) {
    if (user) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  async function createCollection() {
    if (!user) return;

    const name = await promptDialog({
      message: "Name this folder (e.g. Wedding, Casual):",
      confirmText: t("common.save"),
      maxLength: 15
    });
    // A prompt has no length cap of its own unless one is set — an unbounded name would
    // sit awkwardly in the folder tabs row and, before now, had nothing
    // stopping it at the DB either. 15 chars is enough for a real folder
    // name (the placeholder examples are 6-7) while still fitting the pill
    // row without wrapping; the pill itself also truncates with an ellipsis
    // as a second line of defense for names already in the database.
    const clean = name?.trim().slice(0, 15);
    if (!clean) return;

    const { data, error } = await supabase
      .from("collections")
      .insert({ user_id: user.id, name: clean })
      .select("id, name")
      .single();

    if (error) {
      console.log("CREATE COLLECTION ERROR:", error);
      setToast("Couldn't create folder. Try again.");
      return;
    }

    setCollections(prev => [...prev, data]);
  }

  async function renameCollection(collection) {
    setCollectionMenuOpen(null);

    const name = await promptDialog({
      message: "Rename this folder:",
      defaultValue: collection.name,
      confirmText: t("common.save"),
      maxLength: 15
    });
    const clean = name?.trim().slice(0, 15);
    if (!clean || clean === collection.name) return;

    const { error } = await supabase
      .from("collections")
      .update({ name: clean })
      .eq("id", collection.id);

    if (error) {
      console.log("RENAME COLLECTION ERROR:", error);
      setToast("Couldn't rename folder. Try again.");
      return;
    }

    setCollections(prev => prev.map(c => (c.id === collection.id ? { ...c, name: clean } : c)));
  }

  async function deleteCollection(collection) {
    setCollectionMenuOpen(null);

    const ok = await confirmDialog({
      message: `Delete "${collection.name}"? The designs in it stay saved — this only removes the folder.`,
      confirmText: t("common.delete"),
      danger: true
    });
    if (!ok) return;

    const { error } = await supabase.from("collections").delete().eq("id", collection.id);

    if (error) {
      console.log("DELETE COLLECTION ERROR:", error);
      setToast("Couldn't delete folder. Try again.");
      return;
    }

    setCollections(prev => prev.filter(c => c.id !== collection.id));
    // Was viewing the folder that just got deleted — fall back to "All
    // Saved" rather than continuing to show a now-nonexistent collection.
    if (activeCollectionId === collection.id) {
      setActiveCollectionId(null);
    }
  }

  async function openCollectionPicker(designId, anchorRect) {
    // Clicking a second design's folder icon before the first one's fetch
    // resolves used to let whichever response landed last win — a slow,
    // stale lookup for the FIRST design could overwrite the membership
    // checkboxes now showing for a totally different, currently-open design.
    const requestId = ++membershipRequestId.current;

    setOpenCollectionPickerFor(designId);
    setPickerAnchorRect(anchorRect);

    if (collections.length === 0) {
      setPickerMembership({});
      return;
    }

    const { data, error } = await supabase
      .from("collection_items")
      .select("collection_id")
      .eq("design_id", designId)
      .in("collection_id", collections.map(c => c.id));

    if (requestId !== membershipRequestId.current) return;

    if (error) {
      console.log("LOAD MEMBERSHIP ERROR:", error);
      return;
    }

    const membership = {};
    (data || []).forEach(r => {
      membership[r.collection_id] = true;
    });
    setPickerMembership(membership);
  }

  function closeCollectionPicker() {
    setOpenCollectionPickerFor(null);
    setPickerAnchorRect(null);
  }

  async function toggleCollectionMembership(designId, collectionId) {
    // A checkbox click fast enough to land before the previous click's
    // response re-renders would otherwise read the same stale
    // pickerMembership value twice and fire the same insert/delete twice —
    // best case a harmless duplicate row, worst case the second one 23505s
    // and its error-revert flips the checkbox back off even though the
    // first request already succeeded and the item really is in the folder.
    const busyKey = `${designId}:${collectionId}`;
    if (toggleMembershipBusyRef.current.has(busyKey)) return;
    toggleMembershipBusyRef.current.add(busyKey);

    const isMember = !!pickerMembership[collectionId];

    // Optimistic — but reverted below if the write actually fails, so a
    // rejected insert/delete can't leave the picker showing membership that
    // was never actually saved (or vice versa).
    setPickerMembership(prev => ({ ...prev, [collectionId]: !isMember }));

    if (isMember) {
      const { error } = await supabase
        .from("collection_items")
        .delete()
        .eq("collection_id", collectionId)
        .eq("design_id", designId);

      toggleMembershipBusyRef.current.delete(busyKey);

      if (error) {
        console.log("REMOVE FROM COLLECTION ERROR:", error);
        setPickerMembership(prev => ({ ...prev, [collectionId]: true }));
        setToast("Couldn't remove from folder. Try again.");
        return;
      }

      // Viewing that exact folder right now — drop the card immediately.
      if (activeCollectionId === collectionId) {
        setDesigns(prev => prev.filter(d => d.id !== designId));
      }
    } else {
      const { error } = await supabase.from("collection_items").insert({
        collection_id: collectionId,
        design_id: designId
      });

      toggleMembershipBusyRef.current.delete(busyKey);

      if (error) {
        console.log("ADD TO COLLECTION ERROR:", error);
        setPickerMembership(prev => ({ ...prev, [collectionId]: false }));
        setToast("Couldn't add to folder. Try again.");
      }
    }
  }

  if (!user && !loading) {
    return (
      <p style={gate}>
        <IoLockClosedOutline style={{ verticalAlign: -2, marginRight: 6 }} />
        {t("saved.loginGate")}
      </p>
    );
  }

  return (
    <div style={page}>
      <style>{".saved-folder-row::-webkit-scrollbar { display: none; }"}</style>

      {/* Title and "New folder" share one line; the folders below stay on a
          single line that scrolls sideways instead of wrapping into rows. */}
      <div style={headerRow}>
        <h1 style={title}>
          <PiBookmarkSimpleFill size={20} style={{ color: "var(--accent-primary)", flexShrink: 0 }} />
          <span style={titleText}>{t("saved.title")}</span>
        </h1>
        <button style={collectionNewPill} onClick={createCollection} aria-label="New folder">
          <IoAdd size={16} /> New folder
        </button>
      </div>

      <div
        className="saved-folder-row"
        style={collectionRow}
        ref={folderRowRef}
      >
        <button
          data-active={activeCollectionId === null ? "true" : undefined}
          style={activeCollectionId === null ? collectionPillActive : collectionPill}
          onClick={() => setActiveCollectionId(null)}
        >
          All Saved
        </button>
        {collections.map(c => {
          const isActive = activeCollectionId === c.id;
          return (
            <span
              key={c.id}
              data-active={isActive ? "true" : undefined}
              style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}
              ref={collectionMenuOpen === c.id ? collectionMenuRef : null}
            >
              <button
                style={isActive ? collectionPillActive : collectionPill}
                onClick={() => setActiveCollectionId(c.id)}
                title={c.name}
              >
                {c.name}
              </button>

              {/* Rename/delete only make sense for a folder you're already
                  looking at — showing the ⋮ on every pill in the row would
                  clutter it for no reason. */}
              {isActive && (
                <button
                  style={collectionMenuTrigger}
                  onClick={e => {
                    if (collectionMenuOpen === c.id) {
                      setCollectionMenuOpen(null);
                      return;
                    }
                    const r = e.currentTarget.getBoundingClientRect();
                    const MENU_W = 130;
                    setCollectionMenuPos({
                      top: r.bottom + 4,
                      left: Math.min(Math.max(8, r.right - MENU_W), window.innerWidth - MENU_W - 8)
                    });
                    setCollectionMenuOpen(c.id);
                  }}
                  aria-label="Folder options"
                >
                  <IoEllipsisVertical size={14} />
                </button>
              )}

              {collectionMenuOpen === c.id && collectionMenuPos && (
                <div style={{ ...collectionMenuBox, position: "fixed", top: collectionMenuPos.top, left: collectionMenuPos.left, right: "auto" }}>
                  <div style={collectionMenuItem} onClick={() => renameCollection(c)}>
                    Rename
                  </div>
                  <div
                    style={{ ...collectionMenuItem, color: "var(--accent-danger)", borderBottom: "none" }}
                    onClick={() => deleteCollection(c)}
                  >
                    Delete
                  </div>
                </div>
              )}
            </span>
          );
        })}
      </div>

      {loading ? (
        <PostSkeleton />
      ) : designs.length === 0 ? (
        <p style={mutedText}>
          {activeCollectionId
            ? "Nothing in this folder yet — use the folder icon on a saved design to add it here."
            : t("saved.empty")}
        </p>
      ) : (
        <div style={grid}>
          {designs.map(design => {
            function updateDesign(fields) {
              // This page's whole point is "designs I've saved" — if the
              // update is SaveButton reporting this one just got unsaved,
              // a plain field merge would leave it sitting in the list
              // (still showing as unsaved) until the next full reload.
              if (fields._savedByMe === false) {
                setDesigns(prev => prev.filter(d => d.id !== design.id));
                return;
              }
              setDesigns(prev =>
                prev.map(d => (d.id === design.id ? { ...d, ...fields } : d))
              );
            }

            return (
            <div key={design.id} style={card}>
              <div style={cardHeader}>
                <Link
                  to={design.user_id === user?.id ? "/profile" : `/u/${design.user_id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flex: 1,
                    minWidth: 0,
                    textDecoration: "none",
                    color: "var(--text-primary)"
                  }}
                >
                  <LazyImage
                    src={design.users?.avatar_url || "/default-avatar.svg"}
                    alt={`${design.users?.username || "User"}'s avatar`}
                    style={{ ...cardAvatar, flexShrink: 0 }}
                  />
                  <span style={postNameColumn}>
                    <span style={usernameLink}>
                      @{design.users?.username || "unknown"}
                      {design.users?.verified && <VerifiedBadge />}
                    </span>
                    <PostTime date={design.created_at} />
                  </span>
                </Link>
                <div
                  style={{ position: "relative", marginLeft: "auto", flexShrink: 0 }}
                  ref={openMenuId === design.id ? menuRef : null}
                >
                  <button
                    type="button"
                    style={reportBtn}
                    onClick={() => setOpenMenuId(openMenuId === design.id ? null : design.id)}
                    aria-label="More options"
                  >
                    <IoEllipsisVertical />
                  </button>
                  {openMenuId === design.id && (
                    <PostMenu
                      items={[
                        { icon: <IoPricetag />, label: "Tag info", onClick: () => { setOpenMenuId(null); setTagModalId(design.id); } },
                        ...(user?.id && design.user_id !== user.id
                          ? [{ icon: <IoFlag />, label: "Report", onClick: () => { setOpenMenuId(null); setReportModalId(design.id); } }]
                          : [])
                      ]}
                    />
                  )}
                </div>
              </div>

              <div style={imageClip}>
              <ImageCarousel
                images={design.images?.length > 0 ? design.images : [design.image_url]}
                alt={design.title}
                imgStyle={{ ...image, cursor: "zoom-in" }}
                onImageClick={setLightboxImage}
                onDoubleTap={() =>
                  likeDesign({ designId: design.id, user, designOwnerId: design.user_id, onUpdate: updateDesign, likeOnly: true })
                }
              />
              </div>

              <div style={content}>
                <div style={actions}>
                  <LikeButton
                    designId={design.id}
                    likes={design.likes}
                    liked={design._likedByMe}
                    user={user}
                    designOwnerId={design.user_id}
                    onUpdate={updateDesign}
                  />

                  <button
                    onClick={() => requireLogin(() => toggleComments(design.id))}
                    style={commentBadgeBtn}
                    aria-label="Toggle comments"
                  >
                    <PiChatCircleBold size={27} /> <span style={commentCountText}>{design.comment_count || 0}</span>
                  </button>

                  <DownloadButton
                    fileURL={design.design_url}
                    files={design.design_files}
                    designId={design.id}
                    downloads={design.downloads}
                    designOwnerId={design.user_id}
                    user={user}
                    onUpdate={updateDesign}
                  />
                  <ShareButton designId={design.id} />

                  {/* Grouped in one shared flex wrapper (instead of two
                      separate ones like the other action icons) so the
                      actions row's own gap only applies once, between this
                      pair and the share button — not a second time between
                      folder and save themselves. */}
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <button
                      style={folderBtn}
                      onClick={e => openCollectionPicker(design.id, e.currentTarget.getBoundingClientRect())}
                      aria-label="Add to folder"
                    >
                      <IoFolderOutline size={22} />
                    </button>
                    {/* Both this and the folder button are 40px hit-targets
                        with a visibly smaller icon centered inside — with
                        zero gap between them, that leftover internal padding
                        on each side is what was still reading as a gap.
                        Pulling this one in overlaps that padding instead of
                        the icons themselves. */}
                    <span style={{ marginLeft: -10 }}>
                      <SaveButton
                        designId={design.id}
                        saved={design._savedByMe}
                        user={user}
                        onUpdate={updateDesign}
                      />
                    </span>
                  </span>
                </div>

                <p style={caption}><TruncatedText text={design.title} /></p>
                {design.description && (
                  <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
                    {design.description}
                  </p>
                )}

                {openComments[design.id] && (
                  <Comments designId={design.id} onClose={() => toggleComments(design.id)} designOwnerId={design.user_id} />
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {!loading && hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      {loadingMore && <p style={mutedText}>Loading more…</p>}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {toast && <Toast text={toast} onClose={() => setToast("")} />}

      {tagModalId && (
        <TagInfoModal designId={tagModalId} onClose={() => setTagModalId(null)} />
      )}

      {reportModalId && (
        <ReportModal
          reporterId={user?.id}
          targetType="design"
          targetId={reportModalId}
          onClose={() => setReportModalId(null)}
        />
      )}

      {/* Portaled, not rendered inline next to the folder button — nested
          inside a card, an anchored position:absolute popover has no way to
          avoid either getting clipped by the card's own rounded-corner
          overflow, or (once that's fixed) overlapping whatever content sits
          below it on a narrow single-column mobile layout. A portal with a
          backdrop, positioned in the viewport from the button's own
          on-click bounding rect, sidesteps both. */}
      {openCollectionPickerFor && pickerAnchorRect &&
        createPortal(
          <div style={collectionPopoverBackdrop} onClick={closeCollectionPicker}>
            <div
              ref={collectionPopoverRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label="Add to folder"
              style={{
                ...collectionPopover,
                ...getCollectionPopoverPosition(pickerAnchorRect)
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={collectionPopoverHeader}>
                <span>Add to folder</span>
                <button style={collectionPopoverClose} onClick={closeCollectionPicker} aria-label="Close">
                  <IoClose size={17} />
                </button>
              </div>

              {collections.length === 0 ? (
                <p style={{ ...mutedText, padding: "12px 14px" }}>No folders yet.</p>
              ) : (
                // Scrolls instead of growing the popover past a sane height
                // once someone has more than a handful of folders.
                <div style={collectionOptionList}>
                  {collections.map(c => {
                    const checked = !!pickerMembership[c.id];
                    return (
                      <label key={c.id} style={checked ? collectionOptionChecked : collectionOption}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCollectionMembership(openCollectionPickerFor, c.id)}
                          style={collectionCheckbox}
                        />
                        <IoFolderOutline size={15} style={{ flexShrink: 0, color: "var(--text-muted)" }} />
                        <span style={collectionOptionLabel}>{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              <button style={collectionPopoverNewBtn} onClick={createCollection}>
                <IoAdd size={15} /> New folder
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ===== STYLES (Virsa palette) ===== */

const page = {
  padding: "8px 4px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "0 10px 8px"
};

const title = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading)"
};

const titleText = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const gate = { padding: 24, fontSize: 15, color: "var(--text-body)" };
const mutedText = { fontSize: 14, color: "var(--text-muted)" };

const collectionRow = {
  display: "flex",
  flexWrap: "nowrap",
  gap: 8,
  overflowX: "auto",
  padding: "2px 10px",
  marginBottom: 12,
  scrollbarWidth: "none",
  WebkitOverflowScrolling: "touch"
};

const collectionPill = {
  flexShrink: 0,
  padding: "6px 14px",
  borderRadius: 20,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  // Belt-and-suspenders for any folder name already in the database from
  // before the 15-char cap on new ones (or a pasted-in name that slipped
  // past it) — truncates with an ellipsis instead of wrapping/stretching
  // the pill across the row. Full name is still on the button's title.
  maxWidth: 160,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const collectionPillActive = {
  ...collectionPill,
  background: "var(--accent-primary)",
  border: "1.5px solid var(--accent-primary)",
  color: "var(--accent-contrast)"
};

const collectionMenuTrigger = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  marginLeft: 2,
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--text-muted)",
  cursor: "pointer"
};

const collectionMenuBox = {
  position: "absolute",
  top: "calc(100% + 4px)",
  right: 0,
  minWidth: 120,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-input)",
  borderRadius: 10,
  boxShadow: "0 8px 20px rgba(59,10,20,0.15)",
  overflow: "hidden",
  zIndex: 20
};

const collectionMenuItem = {
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-heading)",
  borderBottom: "1px solid var(--border-soft)",
  cursor: "pointer"
};

const collectionNewPill = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  gap: 2,
  padding: "5px 12px",
  borderRadius: 20,
  border: "1.5px dashed var(--border-input)",
  background: "none",
  color: "var(--text-muted)",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer"
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: "10px 18px"
};

const card = {
  background: "var(--bg-surface)",
  borderRadius: 14,
  // NOT overflow:hidden here — the "Add to folder" popover lives further
  // down this same card (in the actions row) and needs to be able to
  // extend past the card's own edge without getting clipped by it. The
  // image is the only part that actually needs clipping (see imageClip
  // below); nothing else here touches a rounded corner.
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)"
};

const imageClip = { overflow: "hidden" };

const reportBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  fontSize: 22,
  cursor: "pointer"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 24,
  padding: "4px 14px"
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)"
};

const image = {
  width: "100%",
  height: "auto",
  display: "block"
};

const content = { padding: "12px 12px 4px" };

const usernameLink = {
  fontWeight: 700,
  color: "var(--text-primary)",
  textDecoration: "none",
  fontSize: 18
};

const caption = {
  margin: "4px 0",
  fontSize: 13.5,
  color: "var(--text-secondary)",
  whiteSpace: "pre-wrap"
};


const actions = {
  display: "flex",
  alignItems: "center",
  gap: 2
};

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  height: 40,
  background: "none",
  border: "none",
  borderRadius: 20,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer"
};

const commentCountText = {
  fontWeight: "bold",
  fontSize: 13,
  color: "var(--text-primary)"
};

const folderBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  width: 40,
  background: "none",
  border: "none",
  color: "var(--text-primary)",
  cursor: "pointer"
};

const collectionPopoverBackdrop = {
  position: "fixed",
  inset: 0,
  background: "transparent",
  zIndex: 1100
};

// Also used to clamp the popover's own height so it never opens off the
// bottom (or top) of the viewport — keep in sync with the padding/rows below.
const COLLECTION_POPOVER_MAX_HEIGHT = 280;
const COLLECTION_POPOVER_WIDTH = 220;
const COLLECTION_POPOVER_MARGIN = 12;

// Anchors the popover right under the folder button that opened it — but if
// there isn't enough room below (the button is near the bottom of the
// screen, as it usually is once you've scrolled a little), flips it to open
// upward instead of just clamping "top" while still rendering as if
// anchored below, which used to leave it floating disconnected from the
// button that opened it, with a random design's content peeking out above.
function getCollectionPopoverPosition(anchorRect) {
  const spaceBelow = window.innerHeight - anchorRect.bottom - COLLECTION_POPOVER_MARGIN;
  const spaceAbove = anchorRect.top - COLLECTION_POPOVER_MARGIN;
  const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;

  const left = Math.min(
    Math.max(anchorRect.right - COLLECTION_POPOVER_WIDTH, COLLECTION_POPOVER_MARGIN),
    window.innerWidth - COLLECTION_POPOVER_WIDTH - COLLECTION_POPOVER_MARGIN
  );

  if (openUpward) {
    return {
      bottom: window.innerHeight - anchorRect.top + 6,
      maxHeight: Math.min(COLLECTION_POPOVER_MAX_HEIGHT, spaceAbove - 6),
      left
    };
  }

  return {
    top: anchorRect.bottom + 6,
    maxHeight: Math.min(COLLECTION_POPOVER_MAX_HEIGHT, spaceBelow - 6),
    left
  };
}

// top/left/bottom/maxHeight are computed per-render by
// getCollectionPopoverPosition and merged in as inline overrides — this
// object only carries the popover's fixed visual styling.
const collectionPopover = {
  position: "fixed",
  width: COLLECTION_POPOVER_WIDTH,
  maxHeight: COLLECTION_POPOVER_MAX_HEIGHT,
  display: "flex",
  flexDirection: "column",
  background: "var(--bg-surface)",
  border: "1px solid var(--border-input)",
  borderRadius: 12,
  boxShadow: "0 10px 28px rgba(59,10,20,0.18)",
  overflow: "hidden"
};

const collectionPopoverHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  padding: "12px 8px 12px 14px",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--text-heading)",
  borderBottom: "1px solid var(--border-soft)"
};

const collectionPopoverClose = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  border: "none",
  background: "none",
  borderRadius: "50%",
  color: "var(--text-muted)",
  cursor: "pointer"
};

const collectionOptionList = {
  overflowY: "auto",
  padding: "4px 6px"
};

const collectionOption = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  fontSize: 13.5,
  color: "var(--text-secondary)",
  padding: "8px 8px",
  borderRadius: 8,
  cursor: "pointer"
};

const collectionOptionChecked = {
  ...collectionOption,
  background: "var(--bg-soft)",
  color: "var(--text-heading)",
  fontWeight: 600
};

const collectionCheckbox = {
  width: 15,
  height: 15,
  flexShrink: 0,
  accentColor: "var(--accent-primary)",
  cursor: "pointer"
};

const collectionOptionLabel = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const collectionPopoverNewBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexShrink: 0,
  width: "100%",
  padding: "11px 14px",
  border: "none",
  borderTop: "1px solid var(--border-soft)",
  background: "none",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--accent-primary)",
  cursor: "pointer"
};
